import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import Select from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useEffect, useState } from "react";
import { useGetCostProjectionByIdQuery } from "services/cost-projection-api";
import {
  useGenerateCostProjectionDocumentMutation,
  useGenerateMSADocumentMutation,
} from "services/msa-api";
import { useToken } from "utils/context";
import { extractErrorMessage } from "utils/error-utils";
import { useConstants } from "utils/hooks";

type GenerateDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  serviceType:
    | "MSA"
    | "MCP"
    | "LCP"
    | "AMENDED_REVIEW"
    | "Pre-MSA Snapshot"
    | "MSA-111"
    | "NTMSA";
  serviceId?: string;
};

type DocumentTypeOption = {
  label: string;
  value: string;
};

type AllocationFilterOption = {
  label: string;
  value: "ALL" | "MEDICARE" | "NON_MEDICARE";
};

const ALLOCATION_FILTER_OPTIONS: AllocationFilterOption[] = [
  { label: "All", value: "ALL" },
  { label: "Medicare", value: "MEDICARE" },
  { label: "Non-Medicare", value: "NON_MEDICARE" },
];

const MSA_FAMILY_TYPES: Record<
  "MSA" | "Pre-MSA Snapshot" | "MSA-111" | "NTMSA" | "AMENDED_REVIEW",
  { primaryType: string; coverLetterType: string }
> = {
  MSA: { primaryType: "MSA", coverLetterType: "MSA_COVER_LETTER" },
  "Pre-MSA Snapshot": {
    primaryType: "MSA",
    coverLetterType: "PRE_MSA_SNAPSHOT_COVER_LETTER",
  },
  "MSA-111": { primaryType: "MSA", coverLetterType: "MSA_111_COVER_LETTER" },
  NTMSA: { primaryType: "MSA", coverLetterType: "MSA_COVER_LETTER" },
  AMENDED_REVIEW: {
    primaryType: "AMENDED_REVIEW_REPORT",
    coverLetterType: "AMENDED_REVIEW_COVER_LETTER",
  },
};

const GenerateDocumentModal = ({
  isOpen,
  onClose,
  serviceType,
  serviceId,
}: GenerateDocumentModalProps) => {
  const { constants } = useConstants();
  const { accessToken } = useToken();
  const [selectedDocType, setSelectedDocType] =
    useState<DocumentTypeOption | null>(null);
  const [selectedAllocationFilter, setSelectedAllocationFilter] =
    useState<AllocationFilterOption>(ALLOCATION_FILTER_OPTIONS[1]); // default to Medicare
  const [generateMSADocument] = useGenerateMSADocumentMutation();
  const [generateCostProjectionDocument] =
    useGenerateCostProjectionDocumentMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Liberty Mutual MCPs generate an FMA document in place of the MCP document
  const { data: costProjection } = useGetCostProjectionByIdQuery(
    { costProjectionId: serviceId ?? "" },
    { skip: serviceType !== "MCP" || !serviceId },
  );
  const usesFmaDocumentType = !!costProjection?.uses_fma_document_type;

  const operationsLetterTypes = new Set(
    constants?.FEATURE_FLAGS?.[
      FEATURE_FLAGS.OPERATIONS_LETTER_GENERATION_ENABLED
    ]
      ? constants?.SUPPORTED_OPERATIONS_DOCUMENT_GENERATION_TYPES || []
      : [],
  );
  const cmsReviewOnlyTypes = new Set(
    constants?.CMS_REVIEW_ONLY_DOCUMENT_GENERATION_TYPES || [],
  );

  // get document types from constants based on service type. This mirrors
  // FutureMedicalDocumentGeneratorRequestSerializer on the backend, which
  // is the actual source of truth and re-validates regardless of what this
  // offers; keep the two in sync by hand if either side's rules change.
  const getDocumentOptions = (): DocumentTypeOption[] => {
    const docTypes = constants?.DOCUMENT_TYPES || [];

    switch (serviceType) {
      case "MSA":
      case "Pre-MSA Snapshot":
      case "MSA-111":
      case "NTMSA":
      case "AMENDED_REVIEW": {
        const { primaryType, coverLetterType } = MSA_FAMILY_TYPES[serviceType];
        // Only MSA and Amended Review are actually submitted to CMS for
        // review, so letters addressed to CMS only make sense there.
        const isCmsSubmittableServiceType =
          serviceType === "MSA" || serviceType === "AMENDED_REVIEW";

        return docTypes
          .filter(
            (type) =>
              type.value === primaryType ||
              type.value === coverLetterType ||
              type.value === "FUTURE_MEDICAL_ALLOCATIONS_EXPORT" ||
              type.value === "FUTURE_MEDICAL_SURGICAL_PRICING_WORKBOOK" ||
              (operationsLetterTypes.has(type.value) &&
                type.value !== "MCP_ACKNOWLEDGEMENT_LETTER" &&
                (isCmsSubmittableServiceType ||
                  !cmsReviewOnlyTypes.has(type.value))),
          )
          .map((type) => ({ label: type.label, value: type.value }));
      }
      case "MCP":
        return docTypes
          .filter(
            (type) =>
              type.value === (usesFmaDocumentType ? "FMA" : "MCP") ||
              type.value === "MCP_COVER_LETTER" ||
              type.value === "FUTURE_MEDICAL_ALLOCATIONS_EXPORT" ||
              type.value === "FUTURE_MEDICAL_SURGICAL_PRICING_WORKBOOK" ||
              (type.value === "MCP_ACKNOWLEDGEMENT_LETTER" &&
                operationsLetterTypes.has(type.value)),
          )
          .map((type) => ({ label: type.label, value: type.value }));
      case "LCP":
        return docTypes
          .filter(
            (type) =>
              type.value === "LCP" ||
              type.value === "LCP_COVER_LETTER" ||
              type.value === "FUTURE_MEDICAL_ALLOCATIONS_EXPORT" ||
              type.value === "FUTURE_MEDICAL_SURGICAL_PRICING_WORKBOOK",
          )
          .map((type) => ({ label: type.label, value: type.value }));
      default:
        return [];
    }
  };

  const documentOptions = getDocumentOptions();

  // set initial selection to the first available document type, and reset it
  // if the available options change underneath it (e.g. MCP swaps to FMA once
  // the cost projection loads)
  useEffect(() => {
    const selectionIsAvailable =
      selectedDocType &&
      documentOptions.some((option) => option.value === selectedDocType.value);

    if (documentOptions.length > 0 && !selectionIsAvailable) {
      setSelectedDocType(documentOptions[0]);
    }
  }, [documentOptions, selectedDocType]);

  const handleGenerate = async () => {
    if (!selectedDocType || !serviceId || !accessToken) {
      return;
    }

    setIsSubmitting(true);

    try {
      let result;
      const allocationFilter = selectedAllocationFilter.value;

      if (
        serviceType === "MSA" ||
        serviceType === "Pre-MSA Snapshot" ||
        serviceType === "MSA-111" ||
        serviceType === "NTMSA"
      ) {
        result = await generateMSADocument({
          msaId: serviceId,
          documentType: selectedDocType.value,
          allocationFilter,
        });
      } else if (serviceType === "AMENDED_REVIEW") {
        result = await generateMSADocument({
          msaId: serviceId,
          documentType: selectedDocType.value,
          allocationFilter,
        });
      } else if (serviceType === "MCP" || serviceType === "LCP") {
        result = await generateCostProjectionDocument({
          costProjectionId: serviceId,
          documentType: selectedDocType.value,
          allocationFilter,
        });
      }

      if (result?.data?.task_id) {
        KaboodleToaster.success("Document generation started.");
        onClose();
      } else {
        KaboodleToaster.error(
          extractErrorMessage(
            result?.error,
            "Failed to start document generation. Please try again.",
          ),
        );
      }
    } catch (error) {
      console.error(error);
      KaboodleToaster.error(
        "Failed to start document generation. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // show allocation filter for all document types except cover letters
  const isCoverLetter = selectedDocType?.value?.includes("COVER_LETTER");
  const isOperationsLetter =
    !!selectedDocType && operationsLetterTypes.has(selectedDocType.value);
  const showAllocationFilter =
    !!selectedDocType && !isCoverLetter && !isOperationsLetter;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleGenerate}
            disabled={!selectedDocType || isSubmitting}
          >
            {isSubmitting ? "Generating..." : "Generate Document"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Generate Document">
        <Select
          label="Select Document Type"
          name="documentType"
          placeholder="Select document type..."
          options={documentOptions}
          value={selectedDocType}
          onChange={(option) =>
            setSelectedDocType(option as DocumentTypeOption)
          }
          required
          isClearable={false}
        />
        {showAllocationFilter && (
          <Select
            label="Allocation Filter"
            name="allocationFilter"
            placeholder="Select allocation filter..."
            options={ALLOCATION_FILTER_OPTIONS}
            value={selectedAllocationFilter}
            onChange={(option) =>
              setSelectedAllocationFilter(option as AllocationFilterOption)
            }
            required
            isClearable={false}
          />
        )}
      </Card>
    </KaboodleModal>
  );
};

export default GenerateDocumentModal;
